/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react";
import { useLocation } from "react-router-dom";
import {
  HelpCircle, BookOpen, Plus, Trash2, Pencil, Check, X,
  Search, ChevronLeft, ChevronRight,
} from "lucide-react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { API_ENDPOINTS } from "@/api/endpoints";
import { toast } from "react-toastify";
import { cn } from "@/lib/utils";

type QuizCategory = "HONOURS" | "RECORDS" | "HISTORY";

interface QuizOption {
  id: number;
  text: string;
  isCorrect: boolean;
  order: number;
}

interface QuizQuestion {
  id: number;
  category: QuizCategory;
  question: string;
  isActive: boolean;
  order: number;
  options: QuizOption[];
}

interface DraftOption {
  text: string;
  isCorrect: boolean;
}

const CATEGORIES: QuizCategory[] = ["HONOURS", "RECORDS", "HISTORY"];

const CATEGORY_META: Record<QuizCategory, { label: string; icon: string; color: string; badge: string }> = {
  HONOURS:  { label: "Honours",  icon: "🏆", color: "ring-amber-500 border-amber-300",  badge: "bg-amber-100 text-amber-700" },
  RECORDS:  { label: "Records",  icon: "📊", color: "ring-blue-500 border-blue-300",    badge: "bg-blue-100 text-blue-700" },
  HISTORY:  { label: "History",  icon: "📖", color: "ring-purple-500 border-purple-300", badge: "bg-purple-100 text-purple-700" },
};

const PAGE_SIZE = 15;

const emptyDraftOption = (): DraftOption => ({ text: "", isCorrect: false });

const emptyForm = {
  category: "HONOURS" as QuizCategory,
  question: "",
  options: [emptyDraftOption(), emptyDraftOption(), emptyDraftOption(), emptyDraftOption()],
};
type FormState = typeof emptyForm;

export default function McqPage() {
  const qc = useQueryClient();
  const location = useLocation();
  const isClubLeagueHistory = location.pathname === "/quiz/club-league-history";

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<QuizQuestion | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<{ question?: string; options?: string }>({});
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<QuizCategory | "all">("all");
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery<QuizQuestion[]>({
    queryKey: ["quiz", "mcq"],
    queryFn: async () => {
      const r = await apiClient.get(API_ENDPOINTS.QUIZ.MCQ_LIST) as unknown as QuizQuestion[] | null;
      return r ?? [];
    },
  });
  const questions = data ?? [];

  const invalidate = () => qc.invalidateQueries({ queryKey: ["quiz", "mcq"] });

  const { mutate: create, isPending: isCreating } = useMutation({
    mutationFn: (d: object) => apiClient.post(API_ENDPOINTS.QUIZ.MCQ_CREATE, d),
    onSuccess: () => { toast.success("Question created"); invalidate(); closeDialog(); },
    onError: (e: any) => toast.error(e.response?.data?.message || "Failed to create"),
  });

  const { mutate: update, isPending: isUpdating } = useMutation({
    mutationFn: ({ id, d }: { id: number; d: object }) =>
      apiClient.patch(API_ENDPOINTS.QUIZ.MCQ_UPDATE(id), d),
    onSuccess: () => { toast.success("Updated"); invalidate(); closeDialog(); },
    onError: (e: any) => toast.error(e.response?.data?.message || "Failed to update"),
  });

  const { mutate: toggle } = useMutation({
    mutationFn: (id: number) => apiClient.post(API_ENDPOINTS.QUIZ.MCQ_TOGGLE(id), {}),
    onSuccess: () => invalidate(),
    onError: () => toast.error("Toggle failed"),
  });

  const { mutate: remove } = useMutation({
    mutationFn: (id: number) => apiClient.delete(API_ENDPOINTS.QUIZ.MCQ_DELETE(id)),
    onSuccess: () => { toast.success("Deleted"); invalidate(); },
    onError: (e: any) => toast.error(e.response?.data?.message || "Failed to delete"),
  });

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, category: categoryFilter !== "all" ? categoryFilter : "HONOURS" });
    setOpen(true);
  };

  const openEdit = (q: QuizQuestion) => {
    setEditing(q);
    setForm({
      category: q.category,
      question: q.question,
      options: [...q.options]
        .sort((a, b) => a.order - b.order)
        .map((o) => ({ text: o.text, isCorrect: o.isCorrect })),
    });
    setOpen(true);
  };

  const closeDialog = () => { setOpen(false); setEditing(null); setErrors({}); };

  const setOption = (i: number, field: keyof DraftOption, value: any) => {
    setForm((f) => {
      const opts = [...f.options];
      if (field === "isCorrect") {
        opts.forEach((_, idx) => { opts[idx] = { ...opts[idx], isCorrect: idx === i }; });
      } else {
        opts[i] = { ...opts[i], [field]: value };
      }
      return { ...f, options: opts };
    });
    setErrors((er) => ({ ...er, options: undefined }));
  };

  const addOption = () => setForm((f) => ({ ...f, options: [...f.options, emptyDraftOption()] }));
  const removeOption = (i: number) => setForm((f) => ({ ...f, options: f.options.filter((_, idx) => idx !== i) }));

  const handleSave = () => {
    const newErrors: typeof errors = {};
    if (!form.question.trim()) newErrors.question = "Question text is required";
    if (form.options.some((o) => !o.text.trim())) newErrors.options = "All option texts are required";
    else if (form.options.filter((o) => o.isCorrect).length !== 1) newErrors.options = "Exactly one correct option must be selected";
    if (Object.keys(newErrors).length) { setErrors(newErrors); return; }
    if (!editing) {
      const dup = questions.find((q) => q.question.trim().toLowerCase() === form.question.trim().toLowerCase());
      if (dup) { setErrors({ question: "A question with this text already exists" }); return; }
    }
    setErrors({});
    if (editing) update({ id: editing.id, d: form });
    else create(form);
  };

  const filtered = questions.filter((q) => {
    if (categoryFilter !== "all" && q.category !== categoryFilter) return false;
    if (!search.trim()) return true;
    return q.question.toLowerCase().includes(search.toLowerCase()) ||
      q.options.some((o) => o.text.toLowerCase().includes(search.toLowerCase()));
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const activeCount = questions.filter((q) => q.isActive).length;

  return (
    <Layout>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            {isClubLeagueHistory
              ? <BookOpen className="h-8 w-8 text-violet-500" />
              : <HelpCircle className="h-8 w-8 text-violet-500" />}
            {isClubLeagueHistory ? "Club League History" : "MCQ Questions"}
          </h1>
          <p className="text-gray-500 mt-1">
            {questions.length} questions · {activeCount} active
          </p>
        </div>
        <Button onClick={openCreate} className="bg-violet-600 hover:bg-violet-700 text-white">
          <Plus className="h-4 w-4 mr-2" />
          New Question
        </Button>
      </div>

      {/* Category filter cards */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        {CATEGORIES.map((cat) => {
          const meta = CATEGORY_META[cat];
          const count = questions.filter((q) => q.category === cat).length;
          const active = questions.filter((q) => q.category === cat && q.isActive).length;
          return (
            <button
              key={cat}
              onClick={() => { setCategoryFilter(categoryFilter === cat ? "all" : cat); setPage(1); }}
              className={cn(
                "bg-white rounded-xl border shadow-sm p-4 text-left transition-all",
                categoryFilter === cat && `ring-2 ${meta.color}`,
              )}
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xl">{meta.icon}</span>
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{meta.label}</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{count}</p>
              <p className="text-xs text-gray-400 mt-0.5">{active} active</p>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="relative max-w-sm mb-4">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search questions or options…"
          className="pl-8 h-9 text-sm"
        />
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-violet-500" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-gray-400">
          <HelpCircle className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p className="font-medium">{search ? "No matches" : "No questions yet"}</p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                  <th className="text-left px-4 py-2.5 font-medium">Question</th>
                  <th className="text-left px-4 py-2.5 font-medium w-28">Category</th>
                  <th className="text-left px-4 py-2.5 font-medium">Correct Answer</th>
                  <th className="text-left px-4 py-2.5 font-medium w-20">Active</th>
                  <th className="px-4 py-2.5 w-20" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paged.map((q) => {
                  const correct = q.options.find((o) => o.isCorrect);
                  return (
                    <tr
                      key={q.id}
                      className={cn("hover:bg-gray-50 transition-colors", !q.isActive && "opacity-50")}
                    >
                      <td className="px-4 py-3">
                        <p className="text-gray-900 leading-snug mb-1.5">{q.question}</p>
                        <div className="flex flex-wrap gap-1">
                          {q.options.map((opt) => (
                            <span
                              key={opt.id}
                              className={cn(
                                "text-[11px] px-2 py-0.5 rounded border",
                                opt.isCorrect
                                  ? "bg-green-50 border-green-200 text-green-700 font-semibold"
                                  : "bg-gray-50 border-gray-200 text-gray-500",
                              )}
                            >
                              {opt.isCorrect && "✓ "}{opt.text}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={cn("text-[10px]", CATEGORY_META[q.category].badge)}>
                          {CATEGORY_META[q.category].icon} {CATEGORY_META[q.category].label}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        {correct ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full">
                            <Check className="h-3 w-3" />
                            {correct.text}
                          </span>
                        ) : (
                          <span className="text-xs text-red-400">None set</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Switch
                          checked={q.isActive}
                          onCheckedChange={() => toggle(q.id)}
                          className="data-[state=checked]:bg-green-500 data-[state=unchecked]:bg-red-400"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 justify-end">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(q)}>
                            <Pencil className="h-3.5 w-3.5 text-gray-400" />
                          </Button>
                          <Button
                            variant="ghost" size="sm" className="h-7 w-7 p-0"
                            onClick={() => { if (confirm("Delete this question?")) remove(q.id); }}
                          >
                            <Trash2 className="h-3.5 w-3.5 text-red-400" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 text-sm text-gray-500">
              <span>
                {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filtered.length)} of {filtered.length}
              </span>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={safePage === 1} onClick={() => setPage((p) => p - 1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((n) => n === 1 || n === totalPages || Math.abs(n - safePage) <= 1)
                  .reduce<(number | "…")[]>((acc, n, idx, arr) => {
                    if (idx > 0 && n - (arr[idx - 1] as number) > 1) acc.push("…");
                    acc.push(n);
                    return acc;
                  }, [])
                  .map((n, i) =>
                    n === "…" ? (
                      <span key={`e-${i}`} className="px-1">…</span>
                    ) : (
                      <Button
                        key={n}
                        variant={n === safePage ? "default" : "outline"}
                        size="sm"
                        className={cn("h-8 w-8 p-0", n === safePage && "bg-violet-600 hover:bg-violet-700 text-white")}
                        onClick={() => setPage(n as number)}
                      >
                        {n}
                      </Button>
                    )
                  )}
                <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={safePage === totalPages} onClick={() => setPage((p) => p + 1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Question" : "New Question"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="mb-1 block text-sm">Category</Label>
              <Select
                value={form.category}
                onValueChange={(v) => setForm((f) => ({ ...f, category: v as QuizCategory }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {CATEGORY_META[cat].icon} {CATEGORY_META[cat].label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="mb-1 block text-sm">
                Question <span className="text-red-500">*</span>
              </Label>
              <textarea
                value={form.question}
                onChange={(e) => { setForm((f) => ({ ...f, question: e.target.value })); if (e.target.value.trim()) setErrors((er) => ({ ...er, question: undefined })); }}
                placeholder="e.g. Which club won the most Premier League titles?"
                rows={3}
                className={cn("w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none", errors.question ? "border-red-400" : "border-gray-300")}
              />
              {errors.question && <p className="text-xs text-red-500 mt-1 font-medium">{errors.question}</p>}
            </div>

            {/* Options */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm">
                  Answer Options
                  <span className="text-gray-400 font-normal ml-1.5">(click ● to mark correct)</span>
                </Label>
                <button onClick={addOption} className="text-xs text-violet-600 hover:underline font-medium">
                  + Add option
                </button>
              </div>
              <div className="space-y-2">
                {form.options.map((opt, i) => (
                  <div key={i} className={cn(
                    "flex items-center gap-2 rounded-lg p-2 border transition-colors",
                    opt.isCorrect ? "bg-green-50 border-green-200" : "bg-gray-50 border-gray-200",
                  )}>
                    <button
                      type="button"
                      onClick={() => setOption(i, "isCorrect", true)}
                      title="Mark as correct"
                      className={cn(
                        "w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors",
                        opt.isCorrect
                          ? "border-green-500 bg-green-500"
                          : "border-gray-300 hover:border-green-400 bg-white",
                      )}
                    >
                      {opt.isCorrect && <Check className="h-3 w-3 text-white" />}
                    </button>
                    <Input
                      value={opt.text}
                      onChange={(e) => setOption(i, "text", e.target.value)}
                      placeholder={`Option ${i + 1}`}
                      className={cn("flex-1 h-8 text-sm border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 px-1",
                        opt.isCorrect && "font-medium text-green-800"
                      )}
                    />
                    {form.options.length > 2 && (
                      <button
                        type="button"
                        onClick={() => removeOption(i)}
                        className="text-gray-400 hover:text-red-500 shrink-0"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {errors.options
                ? <p className="text-xs text-red-500 mt-1.5 font-medium">{errors.options}</p>
                : <p className="text-xs text-gray-400 mt-1.5">Min 2 options, exactly one must be correct.</p>
              }
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>Cancel</Button>
            <Button
              onClick={handleSave}
              disabled={isCreating || isUpdating}
              className="bg-violet-600 hover:bg-violet-700 text-white"
            >
              {isCreating || isUpdating ? "Saving…" : editing ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
